import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { FormField } from '../FormField.jsx';
import { TextInput } from '../TextInput.jsx';
import { SubmitButton } from '../SubmitButton.jsx';
import { BrandingPreview } from './BrandingPreview.jsx';

const DEFAULT_COLOR = '#0B1F36';
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function BrandingSection({ user, organization }) {
  const isAdmin = user?.role === 'admin';
  const tier = organization?.subscription_tier;
  const isFreeTier = tier === 'free';

  if (!isAdmin) {
    return (
      <section className="settings-section">
        <h2>Branding</h2>
        <p className="settings-section-hint">Only organisation admins can manage branding.</p>
      </section>
    );
  }

  if (isFreeTier) {
    return <FreeTierUpgradeCard />;
  }

  return <BrandingForm organization={organization} />;
}

function FreeTierUpgradeCard() {
  return (
    <section className="settings-section settings-branding-upgrade">
      <h2>Branding</h2>
      <div className="settings-upgrade-card" role="region" aria-label="Upgrade to access branding">
        <h3 className="settings-upgrade-headline">Custom branding is a paid feature.</h3>
        <p className="settings-upgrade-body">
          Upload your company logo, set brand colours, and customise the display name on every PDF report.
        </p>
        <a className="settings-upgrade-cta btn btn-primary" href="/pricing">View pricing</a>
      </div>
    </section>
  );
}

function BrandingForm({ organization }) {
  const fileInputRef = useRef(null);
  const [serverState, setServerState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [displayName, setDisplayName] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFilePreviewUrl, setPendingFilePreviewUrl] = useState(null);
  const [colorError, setColorError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsBranding();
        if (cancelled) return;
        setServerState(data);
        setColor(data.primary_color || DEFAULT_COLOR);
        setDisplayName(data.display_name || '');
      } catch (err) {
        if (cancelled) return;
        setLoadError(err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return () => {
      if (pendingFilePreviewUrl) URL.revokeObjectURL(pendingFilePreviewUrl);
    };
  }, [pendingFilePreviewUrl]);

  const initialColor = serverState?.primary_color || DEFAULT_COLOR;
  const initialName = serverState?.display_name || '';
  const colorDirty = color !== initialColor;
  const nameDirty = displayName !== initialName;
  const fileDirty = pendingFile !== null;
  const dirty = colorDirty || nameDirty || fileDirty;

  const previewLogoUrl = useMemo(() => {
    if (pendingFilePreviewUrl) return pendingFilePreviewUrl;
    if (serverState?.logo_url) return `${serverState.logo_url}&cb=${logoCacheBust}`;
    return null;
  }, [pendingFilePreviewUrl, serverState, logoCacheBust]);

  const handleFile = (file) => {
    if (!file) return;
    const accepted = file.type === 'image/png' || file.type === 'image/svg+xml';
    if (!accepted) {
      setGlobalError(new Error('Only PNG and SVG files are accepted.'));
      return;
    }
    if (file.size > 1024 * 1024) {
      setGlobalError(new Error('Logo exceeds 1MB.'));
      return;
    }
    if (pendingFilePreviewUrl) URL.revokeObjectURL(pendingFilePreviewUrl);
    const blobUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setPendingFilePreviewUrl(blobUrl);
    setGlobalError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemoveLogo = async () => {
    setGlobalError(null);
    setSaving(true);
    try {
      const data = await api.deleteSettingsBrandingLogo();
      setServerState(data);
      setLogoCacheBust((x) => x + 1);
      if (pendingFilePreviewUrl) URL.revokeObjectURL(pendingFilePreviewUrl);
      setPendingFile(null);
      setPendingFilePreviewUrl(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setGlobalError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setColorError(null);
    setNameError(null);
    setGlobalError(null);
    setSuccess(false);

    let localFail = false;
    if (colorDirty && !HEX_COLOR_RE.test(color)) {
      setColorError('Use a 6-digit hex like #0B1F36.');
      localFail = true;
    }
    if (nameDirty) {
      const trimmed = displayName.trim();
      if (trimmed.length < 2 || trimmed.length > 60) {
        setNameError('Display name must be 2–60 characters.');
        localFail = true;
      }
    }
    if (localFail) return;

    setSaving(true);
    try {
      let nextState = serverState;
      if (colorDirty || nameDirty) {
        const body = {};
        if (colorDirty) body.primary_color = color;
        if (nameDirty) body.display_name = displayName.trim();
        nextState = await api.patchSettingsBranding(body);
      }
      if (fileDirty && pendingFile) {
        const fd = new FormData();
        fd.append('logo', pendingFile);
        nextState = await api.uploadSettingsBrandingLogo(fd);
        if (pendingFilePreviewUrl) URL.revokeObjectURL(pendingFilePreviewUrl);
        setPendingFile(null);
        setPendingFilePreviewUrl(null);
        setLogoCacheBust((x) => x + 1);
      }
      setServerState(nextState);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        if (err.fieldErrors.primary_color) setColorError(err.fieldErrors.primary_color);
        if (err.fieldErrors.display_name) setNameError(err.fieldErrors.display_name);
        if (!err.fieldErrors.primary_color && !err.fieldErrors.display_name) {
          setGlobalError(err);
        }
      } else {
        setGlobalError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <section className="settings-section">
        <h2>Branding</h2>
        <p className="invite-error" role="alert">Couldn&rsquo;t load branding settings.</p>
      </section>
    );
  }

  if (!serverState) {
    return (
      <section className="settings-section">
        <h2>Branding</h2>
        <p className="form-loading">Loading…</p>
      </section>
    );
  }

  return (
    <section className="settings-section settings-branding">
      <h2>Branding</h2>
      <p className="settings-section-hint">Customise how your organisation appears on PDF reports and the dashboard.</p>

      <div className="settings-branding-layout">
        <form onSubmit={handleSubmit} className="settings-form settings-branding-form" noValidate>
          <FormField label="Logo" htmlFor="branding-logo-input" hint="PNG or SVG, max 1MB. Recommended 400×100px (will display at max-height 60px). For SVGs with custom fonts, convert text to paths/outlines first so the logo renders correctly on PDFs.">
            <div
              className={`settings-branding-dropzone${dragActive ? ' is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              data-testid="branding-dropzone"
            >
              {previewLogoUrl ? (
                <div className="settings-branding-thumb-wrap">
                  <img src={previewLogoUrl} alt="Current logo" className="settings-branding-thumb" />
                  <div className="settings-branding-thumb-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace logo
                    </button>
                    {serverState.logo_url && (
                      <button
                        type="button"
                        className="btn-link-danger"
                        onClick={handleRemoveLogo}
                        disabled={saving}
                      >
                        Remove logo
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="settings-branding-empty">
                  <p>Drop a PNG or SVG here, or</p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                id="branding-logo-input"
                type="file"
                accept="image/png,image/svg+xml"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          </FormField>

          <FormField label="Primary colour" htmlFor="branding-color-text" error={colorError} hint="Used as the accent colour on PDF report covers.">
            <div className="settings-branding-color-row">
              <input
                id="branding-color-picker"
                type="color"
                value={HEX_COLOR_RE.test(color) ? color : DEFAULT_COLOR}
                onChange={(e) => setColor(e.target.value)}
                aria-label="Primary colour picker"
                className="settings-branding-color-input"
              />
              <TextInput
                id="branding-color-text"
                name="primary_color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                error={Boolean(colorError)}
                maxLength={7}
                placeholder={DEFAULT_COLOR}
                aria-label="Primary colour hex value"
              />
            </div>
          </FormField>

          <FormField label="Display name" htmlFor="branding-display-name" error={nameError} hint="Appears on PDF cover and dashboard topbar in place of organisation name.">
            <TextInput
              id="branding-display-name"
              name="display_name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              error={Boolean(nameError)}
              minLength={2}
              maxLength={60}
              placeholder={organization?.name || 'Your organisation'}
            />
          </FormField>

          <div className="settings-form-actions">
            <SubmitButton loading={saving} disabled={!dirty}>
              Save branding
            </SubmitButton>
            {success && (
              <span className="invite-success" role="status">Branding updated.</span>
            )}
            {globalError && (
              <span className="invite-error" role="alert">
                {globalError instanceof ApiError
                  ? (globalError.message || 'Couldn\u2019t update branding.')
                  : (globalError.message || 'Couldn\u2019t update branding.')}
              </span>
            )}
          </div>
        </form>

        <BrandingPreview
          color={HEX_COLOR_RE.test(color) ? color : DEFAULT_COLOR}
          displayName={displayName.trim() || organization?.name || 'Your organisation'}
          logoUrl={previewLogoUrl}
        />
      </div>
    </section>
  );
}
